import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ApplicationConfigurationService } from "@app/core/services/ApplicationConfiguration.service";
import {
  HearingDecisionFinalizeRequest,
  HearingDecisionFinalizeResult,
  HearingRecordDto,
  ProceedingHearingCaptureDto,
  ProceedingHearingCaptureUpsertRequest,
} from "./hearing-record.models";

/**
 * Route family for the cohesive hearing record (#PHMS-HREC S1) and the proceeding-scoped capture
 * (#PHMS-HREC S2). Mirrors the host/quiet-headers conventions of {@link HearingDetailService}.
 */
const HEARING_RECORDS_URL = "/api/v1/phms/hearing-records";

const quietHttpOptions = {
  headers: new HttpHeaders({
    "skip-error-interceptor": "yes",
    "skip-loading-interceptor": "yes",
  }),
  withCredentials: true,
};

/**
 * Client facade for the hearing-record endpoints consumed by the pre-hearing capture surface
 * (#PHMS-HREC S3):
 * - {@link getRecord} → the cohesive S1 record (context + captured + decision + derived).
 * - {@link getCapture} → the current S2 capture for a (proceeding, sequence).
 * - {@link upsertCapture} → a partial, mutation-idempotent upsert of the S2 capture.
 *
 * Uses an absolute API host (apiUrl), not a relative path — the dev proxy's /api rule targets a
 * stale port and 500s relative calls. Mirrors the hearing-detail and case-context services.
 */
@Injectable({ providedIn: "root" })
export class HearingRecordService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(ApplicationConfigurationService);

  /** The cohesive, proceeding-scoped hearing record assembled on read (#PHMS-HREC S1). */
  getRecord(scheduledProceedingId: number): Observable<HearingRecordDto> {
    return this.http.get<HearingRecordDto>(
      this.appConfig.apiUrl(`${HEARING_RECORDS_URL}/${encodeId(scheduledProceedingId)}`),
      quietHttpOptions,
    );
  }

  /**
   * The current S2 capture for the proceeding. `sequence` defaults server-side to 1. Returns 404
   * when no capture has been authored yet; callers should treat that as the empty/initial case.
   */
  getCapture(scheduledProceedingId: number, sequence?: number): Observable<ProceedingHearingCaptureDto> {
    const query = sequence != null ? `?sequence=${encodeURIComponent(String(sequence))}` : "";
    return this.http.get<ProceedingHearingCaptureDto>(
      this.appConfig.apiUrl(`${HEARING_RECORDS_URL}/${encodeId(scheduledProceedingId)}/capture${query}`),
      quietHttpOptions,
    );
  }

  /**
   * Partial upsert of the S2 capture (PUT). Only the provided members are written; a member left
   * null/undefined is not touched, and `narratives` is shallow-merged into the stored object. The
   * server makes this replay-safe (mutation-idempotent), so a debounced autosave can call it freely.
   */
  upsertCapture(
    scheduledProceedingId: number,
    payload: ProceedingHearingCaptureUpsertRequest,
    sequence?: number,
  ): Observable<ProceedingHearingCaptureDto> {
    const body: ProceedingHearingCaptureUpsertRequest =
      sequence != null && payload.sequence == null ? { ...payload, sequence } : payload;
    return this.http.put<ProceedingHearingCaptureDto>(
      this.appConfig.apiUrl(`${HEARING_RECORDS_URL}/${encodeId(scheduledProceedingId)}/capture`),
      body,
      quietHttpOptions,
    );
  }

  /**
   * Finalize the hearing decision (#PHMS-HREC S5). POSTs the panel finding + rationale (and, on an unsuitable
   * finding, the denial length) to the decision endpoint, which records the adjudication and
   * renders the outcome. Kept distinct from {@link upsertCapture} so fact capture and adjudication
   * stay separate.
   */
  finalizeDecision(
    scheduledProceedingId: number,
    payload: HearingDecisionFinalizeRequest,
  ): Observable<HearingDecisionFinalizeResult> {
    return this.http.post<HearingDecisionFinalizeResult>(
      this.appConfig.apiUrl(`${HEARING_RECORDS_URL}/${encodeId(scheduledProceedingId)}/decision`),
      payload,
      quietHttpOptions,
    );
  }
}

function encodeId(value: number): string {
  return encodeURIComponent(String(value));
}
